import React, { useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../../api';
import { ReactComponent as ClipboardListIcon } from '../../../icons/clipboard-list.svg';
import './project-rules-panel.scss';

/**
 * Project rules — durable user-stated policy the agent maintains via the
 * dispatcher's project-rule tools. Read-only here in v1; users mutate
 * rules by talking to the agent ("whenever X, do Y").
 *
 * Mirrors the shape returned by `GET /project-apps/:appId/rules`. Once
 * the @dreamai/types package is republished with ProjectRule the local
 * type can be replaced with the import.
 */
interface ProjectRule {
  id: string;
  text: string;
  source: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

interface ProjectHeartbeatSchedule {
  agenda: string | null;
  intervalMinutes: number | null;
  nextHeartbeatAt: string | null;
}

interface ProjectRulesResponse {
  success: boolean;
  appId: string;
  rules: ProjectRule[];
  heartbeat?: ProjectHeartbeatSchedule;
}

interface ProjectRulesPanelProps {
  appId: string;
}

/**
 * Render an interval in minutes as a fuzzy, human-readable cadence. We
 * deliberately round to the nearest familiar bucket (hour / day / week)
 * because the heartbeat scheduler computes `nextHeartbeatAt` as `now +
 * interval`, so users routinely end up with intervals like 1296 minutes
 * when they ask for "every morning" — saying "about every day" is more
 * useful than the literal number.
 */
function formatCadence(intervalMinutes: number): string {
  if (intervalMinutes <= 0) return '';
  if (intervalMinutes < 60) return `every ${intervalMinutes} minutes`;

  const hours = intervalMinutes / 60;
  if (hours < 22) {
    if (Number.isInteger(hours)) {
      return hours === 1 ? 'every hour' : `every ${hours} hours`;
    }
    return `about every ${Math.round(hours)} hours`;
  }

  const days = intervalMinutes / (60 * 24);
  if (days < 6.5) {
    if (Math.abs(days - Math.round(days)) < 0.1) {
      const rounded = Math.round(days);
      return rounded === 1 ? 'every day' : `every ${rounded} days`;
    }
    return days < 1.5
      ? 'about every day'
      : `about every ${Math.round(days)} days`;
  }

  const weeks = intervalMinutes / (60 * 24 * 7);
  if (Math.abs(weeks - Math.round(weeks)) < 0.1) {
    const rounded = Math.round(weeks);
    return rounded === 1 ? 'every week' : `every ${rounded} weeks`;
  }
  return `about every ${Math.round(weeks)} weeks`;
}

function startOfDay(d: Date): Date {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

/**
 * Format a next-tick timestamp as a relative + absolute hint.
 * "tomorrow at 6:06 AM", "today at 9:00 AM", or "Fri 6 Jun at 9:00 AM".
 */
function formatNextTick(iso: string | null): string | null {
  if (!iso) return null;
  const next = new Date(iso);
  if (Number.isNaN(next.getTime())) return null;

  const time = next.toLocaleString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });

  const today = startOfDay(new Date());
  const target = startOfDay(next);
  const diffDays = Math.round(
    (target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
  );

  if (diffDays === 0) return `today at ${time}`;
  if (diffDays === 1) return `tomorrow at ${time}`;
  if (diffDays > 1 && diffDays < 7) {
    const weekday = next.toLocaleString(undefined, { weekday: 'long' });
    return `${weekday} at ${time}`;
  }

  const date = next.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  return `${date} at ${time}`;
}

const ProjectRulesPanel: React.FC<ProjectRulesPanelProps> = ({ appId }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [rules, setRules] = useState<ProjectRule[] | null>(null);
  const [heartbeat, setHeartbeat] = useState<ProjectHeartbeatSchedule | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchRules = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<ProjectRulesResponse>(
        `/project-apps/${appId}/rules`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setRules(res.data.rules);
      setHeartbeat(res.data.heartbeat ?? null);
    } catch (err) {
      console.error('Error fetching project rules:', err);
      setError('Could not load rules.');
    } finally {
      setLoading(false);
    }
  }, [appId, getAccessTokenSilently]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const hasSchedule = !!heartbeat?.agenda || !!heartbeat?.intervalMinutes;
  const isEmpty = !!rules && rules.length === 0 && !hasSchedule && !error;

  if (isEmpty) {
    return (
      <div className='project-app-empty-state'>
        <div className='project-app-empty-state__card'>
          <div className='project-app-empty-state__icon' aria-hidden='true'>
            <ClipboardListIcon />
          </div>
          <h2 className='project-app-empty-state__title'>No rules yet</h2>
          <p className='project-app-empty-state__description'>
            Your assistant will fill this with the core operating instructions
            of this project you give it so they're always followed.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className='project-rules-panel'>
      <div className='project-rules-panel__header'>
        <h2 className='project-rules-panel__title'>Rules</h2>
        <p className='project-rules-panel__subtitle'>
          Durable instructions the agent will follow on this project. Update
          them by telling the agent — e.g.{' '}
          <em>
            "at 9am every Friday please email me an overview of where we are"
          </em>
          .
        </p>
      </div>

      {loading && !rules && (
        <div className='project-rules-panel__state'>Loading rules…</div>
      )}

      {error && (
        <div className='project-rules-panel__state project-rules-panel__state--error'>
          {error}
        </div>
      )}

      {hasSchedule && (
        <section className='project-rules-panel__schedule'>
          <h3 className='project-rules-panel__section-title'>Schedule</h3>
          {(() => {
            const nextLabel = formatNextTick(
              heartbeat?.nextHeartbeatAt ?? null,
            );
            const cadence = heartbeat?.intervalMinutes
              ? formatCadence(heartbeat.intervalMinutes)
              : '';
            if (!nextLabel && !cadence) return null;
            return (
              <p className='project-rules-panel__schedule-meta'>
                {nextLabel ? (
                  <>
                    Next check-in <strong>{nextLabel}</strong>
                  </>
                ) : (
                  <>Runs {cadence}</>
                )}
                {nextLabel && cadence ? (
                  <span className='project-rules-panel__schedule-cadence'>
                    {' '}
                    ({cadence})
                  </span>
                ) : null}
                .
              </p>
            );
          })()}
          {heartbeat?.agenda ? (
            <p className='project-rules-panel__schedule-agenda'>
              {heartbeat.agenda}
            </p>
          ) : (
            <p className='project-rules-panel__schedule-empty'>
              No agenda yet for each check-in. Tell the agent what to do at each
              tick and it will be recorded here.
            </p>
          )}
        </section>
      )}

      {rules && rules.length > 0 && (
        <ul className='project-rules-panel__list'>
          {rules.map((rule) => (
            <li key={rule.id} className='project-rules-panel__rule'>
              <div className='project-rules-panel__rule-text'>{rule.text}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ProjectRulesPanel;
